import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  booleanAttribute,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy } from '@ngneat/until-destroy';
import { assertExists } from 'common';
import {
  DocumentId,
  DocumentSecureId,
  QueuedDocumentMetadata,
  UserAction,
  UserFriendlyError,
} from 'models';
import { NGXLogger } from 'ngx-logger';
import { derivedAsync } from 'ngxtension/derived-async';
import { Observable, catchError, finalize, map, tap, throwError } from 'rxjs';
import { NotificationService } from 'src/app/services/notification.service';
import { ApplicationQuery } from 'src/app/state/application/application.query';
import { ArchivesQuery } from 'src/app/state/archives/archives.query';
import { DatabasesQuery } from 'src/app/state/databases/databases.query';
import { TaskSearchesService } from 'src/app/state/task-searches/task-searches.service';

/** User action executed event. */
export interface UserActionExecutedEvent {
  /**
   * Function that should be run if the action is cancelled.
   *
   * This does **not** cancel the network request running the action if that has already started.
   */
  cancelAction: () => void;
  /** Name of the action taken. */
  name: string;
  /** Function that will run the action. */
  runAction: () => Observable<void>;
}

/** User Actions Panel. */
@UntilDestroy()
@Component({
  selector: 'app-user-actions-panel',
  templateUrl: './user-actions-panel.component.html',
  styleUrls: ['./user-actions-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserActionsPanelComponent implements OnInit {
  /** Document id. */
  documentId = input.required<number>();
  /** Document secure id. */
  documentSecureId = input.required<string>();
  /**
   * Whether the component should load the queued document metadata from the API.
   *
   * You **must** set the queuedDocumentMetadata input if this is false.
   */
  loadQueueFromApi = input(false, { transform: booleanAttribute });
  /** Emitted when an action is executed. */
  actionExecuted = output<UserActionExecutedEvent>();
  /** Whether to disable the buttons. */
  buttonsDisabled = signal(false);
  /**
   * Queued document metadata. This is required if loadQueueFromApi is false.
   */
  queuedDocumentMetadataInput = input<QueuedDocumentMetadata>(undefined, {
    alias: 'queuedDocumentMetadata',
  });
  /**
   * Does the document have user actions.
   */
  hasActions = computed(() => this.userActions()?.length > 0);
  /** Queue metadata for the document. */
  queuedDocumentMetadata = derivedAsync(() => {
    if (this.loadQueueFromApi()) {
      this.logger.debug('Loading queued document metadata.');
      return this.taskSearchesService.api.getQueuedDocumentMetadata(
        this.databaseId(),
        this.archiveId(),
        this.documentId(),
        this.documentSecureId()
      );
    }
    return this.queuedDocumentMetadataInput();
  });
  /** Should the action panel be shown. */
  shouldShowActions = computed(
    () => !this.appQuery.isReadOnlyLicense && this.hasActions()
  );
  /** User actions for the document. */
  userActions = computed(() => {
    const actions = this.queuedDocumentMetadata()?.actions ?? [];
    return actions.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
  });

  /** User actions with state overrides applied. */
  userActionsWithOverrides = computed(() => {
    const overrides = this.userActionOverrides();
    return this.userActions().map((action) => {
      const override = overrides[action.name];
      return {
        ...action,
        enabled: override ? override.enabled : true,
        disabledMessage: override?.message,
      };
    });
  });

  private archiveId = toSignal(
    this.archivesQuery.activeId$.pipe(
      map((id) => {
        assertExists(id, 'Archive ID must exist.');
        return id;
      })
    ),
    {
      initialValue: this.archivesQuery.activeId,
    }
  );
  private databaseId = toSignal(
    this.databasesQuery.activeDbId$.pipe(
      map((id) => {
        assertExists(id, 'Database ID must exist.');
        return id;
      })
    ),
    { initialValue: this.databasesQuery.activeId }
  );
  private userActionOverrides = toSignal(
    this.appQuery.userActionButtonStateOverrides$,
    { initialValue: this.appQuery.getValue().userActionButtonStateOverrides }
  );

  constructor(
    private logger: NGXLogger,
    private notify: NotificationService,
    private taskSearchesService: TaskSearchesService,
    private appQuery: ApplicationQuery,
    private archivesQuery: ArchivesQuery,
    private databasesQuery: DatabasesQuery
  ) {
    effect(() => {
      this.logger.debug(
        'Queued document metadata: ',
        this.queuedDocumentMetadata()
      );
      this.logger.debug('User actions: ', this.userActions());
    });
  }

  ngOnInit(): void {}

  /**
   * Handler for the run action click event.
   *
   * @param action Action.
   */
  onClickUserAction(action: UserAction): void {
    this.logger.debug('Action button clicked', action);
    this.buttonsDisabled.set(true);
    const documents: Record<DocumentId, DocumentSecureId> = {};
    documents[this.documentId()] = this.documentSecureId();
    const databaseId = this.databaseId();
    const archiveId = this.archiveId();

    const runAction = () => {
      return this.taskSearchesService.userActionsApi
        .runAction(databaseId, archiveId, action.key, documents)
        .pipe(
          finalize(() => this.buttonsDisabled.set(false)), // always re-enable buttons when call completes
          tap(() => {
            this.notify.success('USER_ACTION_SUBMITTED_MESSAGE', {
              userActionName: action.name,
            });
          }),
          catchError((error: UserFriendlyError) => {
            this.notify.error(error);
            // rethrow the error in case something consuming this wants to use it.
            return throwError(() => error);
          })
        );
    };
    this.actionExecuted.emit({
      name: action.name,
      runAction,
      cancelAction: () => {
        this.buttonsDisabled.set(false);
      },
    });
  }
}
